"use client";

import { Button } from "@/components/ui/button";
import { Link } from '@/navigation';
import { Heart, Award, Users, MapPin } from "lucide-react";
import { AnimatedSection } from "@/components/ui/animated-section";
import { useTranslations } from 'next-intl';

type IconName = 'Heart' | 'Award' | 'Users' | 'MapPin';

const iconMap: Record<IconName, React.ComponentType<{ className?: string }>> = {
  Heart,
  Award,
  Users,
  MapPin,
};

interface AboutClientProps {
  values: Array<{
    title: string;
    description: string;
    icon: IconName;
  }>;
  stats: Array<{
    value: string;
    label: string;
  }>;
}

export function AboutClient({ values, stats }: AboutClientProps) {
  const t = useTranslations('ourBaker');
  const tAbout = useTranslations('aboutPage');

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-4 pb-4 md:pt-8 md:pb-8">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <div className="max-w-4xl text-left">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                {tAbout('heroTitle')}
              </h2>
              <p className="hidden md:block text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-6 md:mb-8">
                {tAbout('heroSubtitle')}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl ml-0">
            <AnimatedSection animation="fade-up" delay={0}>
              <div className="prose prose-lg max-w-none">
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                  {tAbout('storyParagraph1')}
                </p>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                  {tAbout('storyParagraph2')}
                </p>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  {tAbout('storyParagraph3')}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Section — data resolved server-side and passed as props for AI bot visibility */}
      <section className="py-12 md:py-16 bg-black text-white">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, i) => (
              <div key={i}>
                <p className="text-3xl md:text-4xl font-bold">{stat.value}</p>
                <p className="text-sm md:text-base text-white/70 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-16 bg-muted/50">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div>
            <AnimatedSection animation="fade-up" delay={0}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-left">
                {tAbout('valuesTitle')}
              </h2>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {values.map((value, index) => {
                const Icon = iconMap[value.icon];
                return (
                  <AnimatedSection
                    key={index}
                    animation="scale"
                    delay={100 + index * 100}
                  >
                    <div className="bg-background p-6 border border-gray-200 hover:border-primary/30 transition-all duration-200 hover:-translate-y-1 group">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="transition-transform duration-200 group-hover:scale-110 group-hover:rotate-3">
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                        </div>
                        <h3 className="text-lg md:text-xl font-semibold">{value.title}</h3>
                      </div>
                      <p className="text-sm md:text-base text-muted-foreground">{value.description}</p>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Team CTA */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8 text-left">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
            {t('meetTeamTitle')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-2xl">
            {t('meetTeamDescription')}
          </p>
          <Button asChild size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 min-h-[44px]">
            <Link href="/about/our-baker">
              {t('meetTeamButton')}
            </Link>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 pb-24 md:pb-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8 text-left">
          <AnimatedSection animation="scale" delay={0}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
              {tAbout('ctaTitle')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 opacity-90">
              {tAbout('ctaDescription')}
            </p>
            <Button asChild size="lg" variant="secondary" className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 min-h-[44px]">
              <Link href="/order">
                {tAbout('ctaButton')}
              </Link>
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}
